/**
 * The Social wall — one org-wide feed of short posts (Engage). Posts carry comments, @mentions,
 * a fixed set of reactions, up to four images, and an optional department audience.
 *
 * Visibility: a post with no `audienceDepartmentIds` is org-wide. A targeted post is visible to
 * people whose CURRENT department (their assignment on their own "today") is one of those, plus its
 * author and anyone with `announcement:manage` (the Engage admins). Everything else answers 404, not
 * 403, so a department-only post's existence is not revealed to outsiders.
 *
 * Pinning is an admin action gated on `announcement:manage` — the Engage admin permission. Posting,
 * reacting and commenting need only a signed-in session (`null`).
 *
 * Mentions are written into the body as `@[Name](person-uuid)` markup. See `social.ts` for the
 * helpers; the server re-derives mentions from the body and drops any that cannot see the post.
 *
 * Images: `POST /images` stores a file and returns its `imageKey`. A post references those keys in
 * `imageKeys`; the bytes are served only through `GET /posts/:postId/images/:index` after a
 * visibility check. Storage keys are never listed on a post DTO.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { SOCIAL_COMMENT_MAX_LENGTH, SOCIAL_POST_MAX_LENGTH } from '../social.js';
import { routeMeta } from './meta.js';

// Pure helpers live in ../social.ts. They are re-exported here so every consumer reaches them
// through `@hrms/shared`, the same way it reaches the contracts.
export * from '../social.js';

const c = initContract();

/** The fixed reaction set. One reaction per person per post; the emoji is never free text. */
export const SOCIAL_REACTIONS = ['👍', '🎉', '❤️', '👏', '💡', '🙌'] as const;
export const socialReactionSchema = z.enum(SOCIAL_REACTIONS);
export type SocialReaction = z.infer<typeof socialReactionSchema>;

export const SOCIAL_IMAGE_MAX_COUNT = 4;

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const socialMentionSchema = z.object({
  personId: uuidSchema,
  name: z.string(),
});
export type SocialMention = z.infer<typeof socialMentionSchema>;

export const socialReactionSummarySchema = z.object({
  emoji: socialReactionSchema,
  count: z.number().int().nonnegative(),
  reactedByMe: z.boolean(),
});
export type SocialReactionSummary = z.infer<typeof socialReactionSummarySchema>;

export const socialImageSchema = z.object({
  index: z.number().int().nonnegative(),
  mimeType: z.string(),
});

export const socialPostSchema = z.object({
  id: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  /** The stored body, mention markup included. Render with `toMentionSegments`, never as HTML. */
  body: z.string(),
  pinned: z.boolean(),
  audienceDepartments: z.array(z.object({ id: uuidSchema, name: z.string() })),
  images: z.array(socialImageSchema),
  reactions: z.array(socialReactionSummarySchema),
  commentCount: z.number().int().nonnegative(),
  mentions: z.array(socialMentionSchema),
  /** True when the viewer wrote the post. */
  isMine: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type SocialPost = z.infer<typeof socialPostSchema>;

export const socialCommentSchema = z.object({
  id: uuidSchema,
  postId: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  mentions: z.array(socialMentionSchema),
  isMine: z.boolean(),
  createdAt: instantSchema,
});
export type SocialComment = z.infer<typeof socialCommentSchema>;

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

const postIdParams = z.object({ postId: uuidSchema });
const commentParams = z.object({ postId: uuidSchema, commentId: uuidSchema });

/** `imageKey` values from POST /images. Tenant-prefixed and validated again by the server. */
export const socialImageKeySchema = z.string().min(1).max(512);

export const listPostsQuerySchema = z.object({
  cursor: uuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /** Only posts that target this department. */
  departmentId: uuidSchema.optional(),
});

// These four body/query schemas are deliberately NOT exported: `createPostBodySchema` /
// `updatePostBodySchema` collide with organisation.contract.ts's own (job-posting) schemas of the
// same name, and `listCommentsQuerySchema` / `createCommentBodySchema` collide with
// goal.contract.ts's (goal-comment) schemas — all four are "post"/"comment" in a different sense.
// Used only within this file to build socialContract below; nothing outside imports them by name.
const createPostBodySchema = z.object({
  body: z.string().trim().min(1).max(SOCIAL_POST_MAX_LENGTH),
  imageKeys: z.array(socialImageKeySchema).max(SOCIAL_IMAGE_MAX_COUNT).default([]),
  audienceDepartmentIds: z.array(uuidSchema).max(50).default([]),
  /** Optional; mentions in the body are always read too. The union is what gets notified. */
  mentionPersonIds: z.array(uuidSchema).max(50).default([]),
});
export type CreatePostBody = z.infer<typeof createPostBodySchema>;

const updatePostBodySchema = z.object({
  body: z.string().trim().min(1).max(SOCIAL_POST_MAX_LENGTH),
});

export const reactBodySchema = z.object({ emoji: socialReactionSchema });

const listCommentsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

const createCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(SOCIAL_COMMENT_MAX_LENGTH),
  mentionPersonIds: z.array(uuidSchema).max(50).default([]),
});
export type CreateCommentBody = z.infer<typeof createCommentBodySchema>;

export const uploadImageBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  contentBase64: z.string().min(1),
});

export const uploadedImageSchema = z.object({
  imageKey: socialImageKeySchema,
  mimeType: z.string(),
});

export const postImageParams = z.object({
  postId: uuidSchema,
  index: z.coerce.number().int().min(0).max(SOCIAL_IMAGE_MAX_COUNT - 1),
});

export const postImageFileSchema = z.object({
  mimeType: z.string(),
  contentBase64: z.string(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/** Signed-in only: the wall is for everyone. */
const member = routeMeta(null);
/** Pinning is an Engage admin action. Same permission the announcement admin screens use. */
const admin = routeMeta('announcement:manage');
/** Base64 bytes are no use in a prompt, and a model cannot produce a real image file. */
const memberNoAi = routeMeta(null, { ai: 'hidden' });

const errors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
};

export const socialContract = c.router(
  {
    listPosts: {
      method: 'GET',
      path: '/posts',
      query: listPostsQuerySchema,
      responses: {
        200: z.object({
          items: z.array(socialPostSchema),
          nextCursor: uuidSchema.nullable(),
        }),
        401: errorBodySchema,
      },
      summary: 'The wall: pinned posts first, then newest, filtered to what the viewer may see',
      metadata: member,
    },
    createPost: {
      method: 'POST',
      path: '/posts',
      body: createPostBodySchema,
      responses: {
        201: socialPostSchema,
        ...errors,
      },
      summary: 'Publish a post to the wall',
      metadata: member,
    },
    updatePost: {
      method: 'PATCH',
      path: '/posts/:postId',
      pathParams: postIdParams,
      body: updatePostBodySchema,
      responses: {
        200: socialPostSchema,
        ...errors,
      },
      summary: "Edit the body of one's own post",
      metadata: member,
    },
    deletePost: {
      method: 'DELETE',
      path: '/posts/:postId',
      pathParams: postIdParams,
      body: c.noBody(),
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        ...errors,
      },
      summary: "Delete one's own post (soft)",
      metadata: member,
    },
    pinPost: {
      method: 'POST',
      path: '/posts/:postId/pin',
      pathParams: postIdParams,
      body: c.noBody(),
      responses: {
        200: socialPostSchema,
        ...errors,
      },
      summary: 'Pin a post to the top of the wall',
      metadata: admin,
    },
    unpinPost: {
      method: 'DELETE',
      path: '/posts/:postId/pin',
      pathParams: postIdParams,
      body: c.noBody(),
      responses: {
        200: socialPostSchema,
        ...errors,
      },
      summary: 'Unpin a post',
      metadata: admin,
    },
    reactToPost: {
      method: 'PUT',
      path: '/posts/:postId/reaction',
      pathParams: postIdParams,
      body: reactBodySchema,
      responses: {
        200: socialPostSchema,
        ...errors,
      },
      summary: "Set the viewer's one reaction on a post (replaces any earlier one)",
      metadata: member,
    },
    removeReaction: {
      method: 'DELETE',
      path: '/posts/:postId/reaction',
      pathParams: postIdParams,
      body: c.noBody(),
      responses: {
        200: socialPostSchema,
        ...errors,
      },
      summary: "Take back the viewer's reaction on a post",
      metadata: member,
    },
    listComments: {
      method: 'GET',
      path: '/posts/:postId/comments',
      pathParams: postIdParams,
      query: listCommentsQuerySchema,
      responses: {
        200: z.object({ items: z.array(socialCommentSchema) }),
        ...errors,
      },
      summary: 'Comments on a post, oldest first',
      metadata: member,
    },
    addComment: {
      method: 'POST',
      path: '/posts/:postId/comments',
      pathParams: postIdParams,
      body: createCommentBodySchema,
      responses: {
        201: socialCommentSchema,
        ...errors,
      },
      summary: 'Comment on a post',
      metadata: member,
    },
    deleteComment: {
      method: 'DELETE',
      path: '/posts/:postId/comments/:commentId',
      pathParams: commentParams,
      body: c.noBody(),
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        ...errors,
      },
      summary: "Delete one's own comment (soft)",
      metadata: member,
    },
    uploadImage: {
      method: 'POST',
      path: '/images',
      body: uploadImageBodySchema,
      responses: {
        201: uploadedImageSchema,
        ...errors,
      },
      summary: 'Store one image for a post (PNG, JPEG or WebP, 5 MB or smaller)',
      metadata: memberNoAi,
    },
    downloadPostImage: {
      method: 'GET',
      path: '/posts/:postId/images/:index',
      pathParams: postImageParams,
      responses: {
        200: postImageFileSchema,
        ...errors,
      },
      summary: 'One image attached to a post the viewer can see',
      metadata: memberNoAi,
    },
  },
  { pathPrefix: '/social' },
);
